
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Auth, Hub } from 'aws-amplify';
import { TranslateResult } from 'vue-i18n';
import AuthGroup from '@/logic/auth/AuthGroup';
import ColorBox from '@/components/sidebar/ColorBox.vue';
import { Endpoint } from '@/logic/auth/Endpoint';
import SapSwitcher from '@/components/sidebar/SapSwitcher.vue';

interface MenuItem {
  route: string;
  icon: string;
  label?: string;
  meta?: any;
}

@Component({
  components: { SapSwitcher, ColorBox },
})
export default class Navigation extends Vue {
  // Make enum available in template
  AuthGroup = AuthGroup;

  menuActive = false;

  loading = false;

  get items(): MenuItem[] {
    const dashboard: MenuItem = {
      route: 'dashboard',
      icon: 'dashboard',
    };
    const eatThis: MenuItem = {
      route: 'eat-this',
      icon: 'eat-this',
    };
    const profile: MenuItem = {
      route: 'profile',
      icon: 'settings',
      label: 'settings.main',
    };
    const items: Record<string, MenuItem[]> = {};
    items[AuthGroup.Multinational] = [
      dashboard,
      {
        route: 'lwm-company-information',
        icon: 'home',
        meta: { generic: true },
      },
      {
        route: 'partnership',
        icon: 'partnerships',
      },
      {
        route: 'approved-products', // portfolio
        icon: 'folder-check',
      },
      {
        route: 'market-activations',
        icon: 'megaphone',
      },
      {
        route: 'trend-information',
        icon: 'graph',
        meta: { generic: true },
      },
      {
        route: 'new-products', // innovations & trends
        icon: 'flask',
      },
      {
        route: 'training', // ops and training
        icon: 'training',
      },
      {
        route: 'sustainability',
        icon: 'trees',
        meta: { generic: true },
      },
      {
        route: 'lwm-contact',
        icon: 'phone',
      },
      {
        route: 'best-practices',
        icon: 'book-open',
      },
      eatThis,
      profile,
    ];

    if (this.hasAccess(undefined, AuthGroup.Multinational)) {
      return items[AuthGroup.Multinational];
    }

    return [dashboard, eatThis];
  }

  get logo(): string {
    if (this.hasAccess(undefined, AuthGroup.Multinational)) {
      const img = this.$store.state.content.multinationalContent?.content?.logo;
      if (img) {
        // @ts-ignore
        return this.$helpers.getApiImageThumbnail(img);
      }
    }
    return '/img/logo.svg';
  }

  get isImpersonating(): boolean {
    return this.$store.getters['auth/isImpersonating'];
  }

  beforeCreate() {
    this.$store.dispatch('auth/getUser');
    Hub.listen('auth', () => {
      this.$store.dispatch('auth/getUser');
    });
  }

  get signedIn(): boolean {
    return this.$store.getters['auth/signedIn'];
  }

  // menuTo can be a route path or route name
  isActive(menuTo: string): boolean {
    return this.$route.path === menuTo || this.$route.name === menuTo;
  }

  renderMenuItemLabel(item: any): TranslateResult {
    return this.$t(`navigation.${item.label || item.route}`, {
      company: this.$store.state.content.multinationalContent?.content?.title,
    });
  }

  isExpanded(startsWith: string): boolean {
    return this.$route.path.startsWith(startsWith);
  }

  // Check access
  hasAccess(endpoint?: Endpoint, group?: string): boolean {
    return this.$store.getters['auth/hasAccess'](endpoint, group);
  }

  multinationalSlug(item: any): string {
    const slug = item.meta?.generic
      ? 'generic'
      : this.$store.getters['auth/multinationalSlug'];
    return `/content/multinational_portal/*/${slug}/${item.route}`;
  }

  toggleMenu(active?: boolean) {
    this.menuActive = active !== undefined ? active : !this.menuActive;
  }

  async signOut() {
    this.loading = true;
    await Auth.signOut({ global: true });
    this.loading = false;
    this.toggleMenu(false);
  }

  @Watch('$route')
  routeChanged() {
    this.toggleMenu(false);
  }

  @Watch('$store.state.auth.userRoles', { immediate: true })
  groupsChange() {
    if (this.hasAccess(undefined, AuthGroup.Multinational)) {
      this.$store.dispatch('content/fetchMultinationalContent', {
        i18n: this.$i18n,
        multinational: this.$store.getters['auth/multinationalSlug'],
      });
    }
  }
}
